import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import Icon from '../components/Icon';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { apiFetch } from '../lib/api';
import type { HealthResponse } from '../types/api';

type HealthState = 'loading' | 'ok' | 'error';

const STAT_CARDS: Array<{ label: string; value: string; hint: string }> = [
  { label: 'حساب متصل', value: '—', hint: 'اتصال اینستاگرام' },
  { label: 'اتومیشن‌های فعال', value: '—', hint: 'از کل اتومیشن‌ها' },
  { label: 'پیام‌های پردازش‌شده', value: '—', hint: 'از زمان اتصال' },
  { label: 'کامنت‌های پردازش‌شده', value: '—', hint: 'از زمان اتصال' },
  { label: 'اکشن‌های موفق', value: '—', hint: 'پاسخ خودکار' },
  { label: 'اکشن‌های ناموفق', value: '—', hint: 'نیازمند بررسی' },
];

export default function DashboardPage() {
  useDocumentTitle('داشبورد');
  const [health, setHealth] = useState<HealthState>('loading');

  useEffect(() => {
    let cancelled = false;

    apiFetch<HealthResponse>('/api/health')
      .then(() => {
        if (!cancelled) setHealth('ok');
      })
      .catch(() => {
        if (!cancelled) setHealth('error');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const healthChip =
    health === 'ok'
      ? { dot: 'bg-emerald-500', text: 'سرویس فعال' }
      : health === 'error'
        ? { dot: 'bg-rose-500', text: 'خطا در اتصال به سرویس' }
        : { dot: 'bg-slate-400', text: 'در حال بررسی وضعیت…' };

  return (
    <div className="space-y-6">
      <PageHeader
        title="داشبورد"
        description="نمای کلی حساب متصل، اتومیشن‌ها و فعالیت‌های اخیر"
        actions={
          <button
            type="button"
            disabled
            title="در فاز بعدی فعال می‌شود"
            className="inline-flex cursor-not-allowed items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white opacity-60"
          >
            <Icon name="plus" className="h-4 w-4" />
            ایجاد اتومیشن
          </button>
        }
      />

      <div className="flex items-center gap-2 text-sm text-slate-600">
        <span className={`h-2.5 w-2.5 rounded-full ${healthChip.dot}`} aria-hidden="true" />
        <span>{healthChip.text}</span>
        <span className="text-xs text-slate-400">(GET /api/health)</span>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {STAT_CARDS.map((card) => (
          <StatCard key={card.label} label={card.label} value={card.value} hint={card.hint} />
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900">فعالیت‌های اخیر</h3>
        <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-8 text-center">
          <p className="text-sm text-slate-500">هنوز رویدادی ثبت نشده است.</p>
          <p className="mt-1 text-xs text-slate-400">
            پس از اتصال حساب اینستاگرام و دریافت رویدادها، آخرین فعالیت‌ها اینجا نمایش داده می‌شوند.
          </p>
        </div>
      </div>
    </div>
  );
}
